import { useCallback, useEffect, useState } from "react";
import { Copy, Loader2, CheckCircle2, Clock, XCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { publicApi, errMsg } from "@/lib/api";
import { inr, fmtDateTime } from "@/lib/format";

const CLAIM_STYLE = {
  pending: ["bg-amber-50 text-amber-800", Clock, "Verifying"],
  approved: ["bg-emerald-50 text-emerald-800", CheckCircle2, "Payment received"],
  rejected: ["bg-rose-50 text-rose-700", XCircle, "Not found"],
};

function ClaimRow({ c }) {
  const [cls, Icon, label] = CLAIM_STYLE[c.status];
  return (
    <div className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm ${cls}`} data-testid={`utr-claim-${c.id}`}>
      <div><div className="font-semibold">{inr(c.amount)} · UTR {c.utr}</div><div className="text-xs opacity-80">{fmtDateTime(c.created_at)}{c.status === "rejected" && c.reason ? ` · ${c.reason}` : ""}</div></div>
      <span className="inline-flex items-center gap-1 text-xs font-semibold"><Icon className="h-4 w-4" />{label}</span>
    </div>
  );
}

export const UpiPayCard = ({ token, st }) => {
  const [amount, setAmount] = useState(st.balance > 0 ? String(st.balance) : "");
  const [utr, setUtr] = useState("");
  const [claims, setClaims] = useState([]);
  const [busy, setBusy] = useState(false);
  const [other, setOther] = useState(false);
  const load = useCallback(() => publicApi.get(`/${token}/utr`).then((r) => setClaims(r.data)).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);
  if (!st.business.upi_id) return null;
  const biz = st.business;
  const submit = async () => {
    setBusy(true);
    try {
      await publicApi.post(`/${token}/utr`, { amount: +amount, utr });
      toast.success("Thanks! We'll verify your payment and update your bill shortly.");
      setUtr("");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 rounded-3xl border border-[#E7E2D6] bg-white p-5" data-testid="upi-pay-card">
      <div className="text-xs uppercase tracking-[0.2em] text-[#D96B43]">Step 1 · Pay</div>
      <div className="font-display text-lg font-semibold text-[#0F382C] mt-1">Scan & pay with PhonePe / any UPI app</div>
      {st.upi_link ? (
        <>
          <div className="mx-auto mt-4 w-fit rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid="upi-amount-qr">
            <QRCodeSVG value={st.upi_link} size={220} level="M" />
          </div>
          <div className="mt-2 text-center text-sm">Amount filled in: <b className="font-mono" data-testid="upi-qr-amount">{inr(st.balance)}</b></div>
          {biz.upi_qr_url && (
            <button type="button" onClick={() => setOther(!other)} className="mx-auto mt-2 flex items-center gap-1 text-xs font-semibold text-[#0F382C] underline" data-testid="upi-other-amount-toggle">
              {other ? "Hide" : "Pay a different amount"}
            </button>
          )}
          {other && <img src={biz.upi_qr_url} alt="PhonePe UPI QR" className="mx-auto mt-3 w-full max-w-[240px] rounded-2xl bg-black" data-testid="upi-qr-image" />}
        </>
      ) : biz.upi_qr_url && <img src={biz.upi_qr_url} alt="PhonePe UPI QR" className="mx-auto mt-4 w-full max-w-[260px] rounded-2xl bg-black" data-testid="upi-qr-image" />}
      <div className="mt-3 text-center text-sm font-semibold" data-testid="upi-payee-name">{biz.upi_name}</div>
      <button className="mx-auto mt-1 flex items-center gap-1 rounded-full bg-[#FAF4E8] px-3 py-1.5 text-sm font-mono text-[#0F382C]" onClick={() => { navigator.clipboard?.writeText(biz.upi_id); toast.success("UPI ID copied"); }} data-testid="portal-copy-upi">
        <Copy className="h-3.5 w-3.5" />{biz.upi_id}
      </button>
      {st.upi_link && <a href={st.upi_link} className="mt-4 block w-full rounded-2xl bg-gradient-to-r from-purple-700 to-indigo-700 py-4 text-center font-bold text-white shadow-md" data-testid="portal-upi-pay-btn">Pay {inr(st.balance)} in UPI app</a>}
      <div className="mt-6 border-t border-[#E7E2D6] pt-5">
        <div className="text-xs uppercase tracking-[0.2em] text-[#D96B43]">Step 2 · Confirm</div>
        <div className="font-display text-lg font-semibold text-[#0F382C] mt-1">Submit your UTR number</div>
        <p className="text-xs text-muted-foreground mt-1">After paying, open PhonePe → History → tap the payment and copy the 12-digit <b>UTR / UPI Ref. No.</b></p>
        <div className="mt-3 grid grid-cols-5 gap-2">
          <div className="col-span-2 space-y-1"><Label>Amount (₹)</Label><Input type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12" data-testid="utr-amount-input" /></div>
          <div className="col-span-3 space-y-1"><Label>UTR number</Label><Input inputMode="numeric" maxLength={12} value={utr} onChange={(e) => setUtr(e.target.value.replace(/\D/g, ""))} placeholder="12 digits" className="h-12 font-mono" data-testid="utr-number-input" /></div>
        </div>
        <Button disabled={busy || utr.length !== 12 || !(+amount > 0)} onClick={submit} className="mt-3 h-12 w-full rounded-xl bg-[#0F382C] hover:bg-[#134E3A]" data-testid="utr-submit-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" />Submit payment details</>}
        </Button>
        {claims.length > 0 && <div className="mt-4 space-y-2" data-testid="utr-claims-list">{claims.map((c) => <ClaimRow key={c.id} c={c} />)}</div>}
      </div>
    </div>
  );
};
